import { createSignal, onMount, createEffect } from 'solid-js';
import ModalBase from './ModalBase';
import GlowCard from '../ui/GlowCard';
import GlowButton from '../ui/GlowButton';
import { showToast } from '../ui/ToastNotification';
import { isRealMode } from '../../../api';
import { gameStore } from '../../../store/gameStore';

interface Props { open: boolean; onClose: () => void; }

function fmtMult(v: number) {
  return v > 1e6 ? `${v.toExponential(2)}x` : `${v.toFixed(2)}x`;
}

export default function ProvablyFairModal(props: Props) {
  const [serverHash, setServerHash] = createSignal('');
  const [clientSeed, setClientSeed] = createSignal('');
  const [gameSeed, setGameSeed] = createSignal('');
  const [result, setResult] = createSignal('--');
  const [verifying, setVerifying] = createSignal(false);
  const [verifiedFlash, setVerifiedFlash] = createSignal(false);
  const [copied, setCopied] = createSignal<string | null>(null);

  const refresh = () => {
    if (isRealMode) {
      // Read provably fair data from gameStore signals
      setServerHash(gameStore.serverSeedHash() || '');
      setClientSeed(gameStore.fairClientSeed() || '');

      const lastCrash = gameStore.lastCrash();
      if (lastCrash) {
        // After a crash, the server seed is revealed
        setGameSeed(gameStore.revealedServerSeed() || 'Not revealed yet');
        setResult(fmtMult(lastCrash.crashPoint));
      } else {
        setGameSeed('Not revealed yet');
        setResult('--');
      }
      return;
    }
    // Mock mode: read from local Game
    try {
      const round = (window as any).memePumpApp?.getGame?.()?.getCurrentRound?.();
      if (round) {
        setServerHash((window as any).memePumpApp?.hashString?.(round.serverSeed) || '');
        setClientSeed(round.clientSeed || '');
        setGameSeed(round.gameSeed || 'Not revealed yet');
        setResult(round.multiplier ? fmtMult(round.multiplier) : '--');
      }
    } catch {}
  };

  onMount(refresh);
  createEffect(() => { if (props.open) refresh(); });

  const copy = async (key: string, text: string) => {
    try { await navigator.clipboard.writeText(text); setCopied(key); setTimeout(() => setCopied(null), 900); } catch {}
  };

  const verify = async () => {
    setVerifying(true);
    try {
      const concat = `${serverHash()}${clientSeed()}${gameSeed()}`;
      const data = new TextEncoder().encode(concat);
      const hashBuffer = await crypto.subtle.digest('SHA-256', data);
      const arr = Array.from(new Uint8Array(hashBuffer)).slice(0, 8);
      let int64 = 0n;
      for (let i = 0; i < arr.length; i++) int64 = (int64 << 8n) + BigInt(Number(arr[i] ?? 0));
      const r = Number(int64 % 1000000n) / 1000000;
      const mult = 0.99 / (1 - r);
      setResult(fmtMult(mult));
      setVerifiedFlash(true);
      setTimeout(() => setVerifiedFlash(false), 700);
      showToast('success','Verification calculated');
    } catch {
      showToast('error','Failed to verify');
    } finally { setVerifying(false); }
  };

  const regenClientSeed = () => {
    const seed = Math.random().toString(36).substr(2, 16);
    setClientSeed(seed);
    showToast('success','New client seed generated');
  };

  return (
    <ModalBase id="fair-solid" title="Provably Fair" open={props.open} onClose={props.onClose} size="lg">
      <div class="mp-fair mp-fair-new">
        <GlowCard>
          <div class="mp-card-head"><span class="mp-card-ico">🔐</span><h3 class="mp-card-title">How results are calculated</h3></div>
          <p class="mp-card-text">Each round publishes a hashed server seed before it starts. The result is derived from <strong>server seed</strong> + <strong>client seed</strong> + <strong>game seed</strong>, ensuring fairness.</p>
          <p class="mp-card-text mp-code">HMAC_SHA256(server_seed, client_seed)</p>
        </GlowCard>

        <div class="mp-field-row">
          <div class="mp-field">
            <label class="mp-label">Server Seed (hashed)</label>
            <div class="mp-value">
              <code>{serverHash() || '—'}</code>
              <button class={`mp-chip ${copied()==='server'?'copied':''}`} onClick={() => copy('server', serverHash())}>{copied()==='server'?'Copied!':'Copy'}</button>
            </div>
          </div>
          <div class="mp-field">
            <label class="mp-label">Client Seed</label>
            <div class="mp-value">
              <code>{clientSeed() || '—'}</code>
              <button class={`mp-chip ${copied()==='client'?'copied':''}`} onClick={() => copy('client', clientSeed())}>{copied()==='client'?'Copied!':'Copy'}</button>
            </div>
          </div>
        </div>
        <div class="mp-field-row">
          <div class="mp-field">
            <label class="mp-label">Game Seed</label>
            <div class="mp-value">
              <code>{gameSeed() || '—'}</code>
              <button class={`mp-chip ${copied()==='game'?'copied':''}`} onClick={() => copy('game', gameSeed())}>{copied()==='game'?'Copied!':'Copy'}</button>
            </div>
          </div>
          <div class="mp-field">
            <label class="mp-label">Result Multiplier</label>
            <div class={`mp-value mp-result ${verifiedFlash() ? 'verified' : ''}`}>
              <code>{result()}</code>
              <span aria-hidden="true" class="mp-chip mp-spacer">Copy</span>
            </div>
          </div>
        </div>

        <div class="mp-actions">
          <GlowButton class="mp-primary" onClick={verify} ariaLabel="Verify last round">
            {verifying() ? 'Verifying…' : 'Verify Last Round'}
          </GlowButton>
          <button class="mp-chip" onClick={regenClientSeed}>New Client Seed</button>
        </div>
        <p class="mp-note">You can verify any round result using the public hash above.</p>
      </div>
    </ModalBase>
  );
}
